/**
 * Sales ticket — PURE construction of receipt from order.
 *
 * Two outputs from SAME document:
 * - `renderReceiptText()` → texto plano monoespaciado (preview en pantalla,
 *   copy/paste, tests legibles);
 * - `encodeReceiptEscPos()` → ESC/POS payload ready for thermal printer
 *   (see `escpos.ts`; device transmission is manual phase).
 *
 * Todo el layout es determinista: ancho fijo en columnas (42 = 80 mm,
 * 32 = 58 mm), money in minor units formatted with `formatMoney`.
 */

import { formatMoney } from "@fleet/utils/format-money";
import { EscPosBuilder } from "./escpos";

/** Sellable ticket line (already filtered: no voided lines). */
export interface ReceiptItem {
  name: string;
  qty: number;
  /** Precio unitario EFECTIVO en centavos (modificadores incluidos). */
  unitPrice: number;
  /** Line total in cents. */
  lineTotal: number;
}

export interface ReceiptPayment {
  method: string;
  amount: number;
  tip?: number;
  tendered?: number;
  changeGiven?: number;
}

export interface ReceiptData {
  restaurantName: string;
  branchName?: string | null;
  /** Order ID (printed shortened to 8 chars). */
  orderId: string;
  /** ISO timestamp of closing/charging (or opening if still open). */
  timestamp?: string | null;
  tableName?: string | null;
  employeeName?: string | null;
  items: ReceiptItem[];
  subtotal: number;
  discount: number;
  taxTotal: number;
  total: number;
  payments: ReceiptPayment[];
  /** Footer message ("Thank you for visiting!") — translated by caller. */
  footer?: string | null;
  /** Translated labels (module is pure: does not import i18n). */
  labels?: Partial<ReceiptLabels>;
}

export interface ReceiptLabels {
  order: string;
  table: string;
  server: string;
  subtotal: string;
  discount: string;
  tax: string;
  total: string;
  tendered: string;
  change: string;
  tip: string;
}

const DEFAULT_LABELS: ReceiptLabels = {
  order: "Order",
  table: "Table",
  server: "Server",
  subtotal: "Subtotal",
  discount: "Discount",
  tax: "Tax",
  total: "TOTAL",
  tendered: "Tendered",
  change: "Change",
  tip: "Tip",
};

/** Line style, for ESC/POS encoder to apply emphasis/size. */
export type ReceiptLineStyle = "title" | "bold" | "normal";

export interface ReceiptLine {
  text: string;
  style: ReceiptLineStyle;
  align: "left" | "center";
}

/** Ancho en columnas: 42 ≈ 80 mm, 32 ≈ 58 mm. */
export const RECEIPT_WIDTH_80MM = 42;
export const RECEIPT_WIDTH_58MM = 32;

/** Distributes `left` and `right` across `width` cols (right-aligned). */
export function twoColumns(left: string, right: string, width: number): string {
  const space = width - right.length;
  if (space <= 0) {
    return right.slice(0, width);
  }
  const l = left.length > space - 1 ? `${left.slice(0, Math.max(0, space - 2))}…` : left;
  return l + " ".repeat(Math.max(1, space - l.length)) + right;
}

/** Chunks text into lines of ≤ width (hard wrap, no orphans). */
export function wrapText(text: string, width: number): string[] {
  const out: string[] = [];
  let rest = text;
  while (rest.length > width) {
    out.push(rest.slice(0, width));
    rest = rest.slice(width);
  }
  out.push(rest);
  return out;
}

function divider(width: number): string {
  return "-".repeat(width);
}

/** id corto imprimible (8 chars del UUID). */
export function shortOrderId(orderId: string): string {
  return orderId.replaceAll("-", "").slice(0, 8).toUpperCase();
}

/**
 * Builds ticket document: ordered list of styled lines.
 * Deterministic — same data, same lines.
 */
export function buildReceiptLines(data: ReceiptData, width = RECEIPT_WIDTH_80MM): ReceiptLine[] {
  const labels: ReceiptLabels = { ...DEFAULT_LABELS, ...data.labels };
  const lines: ReceiptLine[] = [];
  const center = (text: string, style: ReceiptLineStyle = "normal") => {
    lines.push({ text, style, align: "center" });
  };
  const left = (text: string, style: ReceiptLineStyle = "normal") => {
    lines.push({ text, style, align: "left" });
  };

  center(data.restaurantName, "title");
  if (data.branchName) {
    center(data.branchName);
  }
  center("");

  left(`${labels.order} #${shortOrderId(data.orderId)}`, "bold");
  if (data.timestamp) {
    // ISO → "YYYY-MM-DD HH:mm" (without runtime TZ dependency).
    left(data.timestamp.slice(0, 16).replace("T", " "));
  }
  if (data.tableName) {
    left(`${labels.table}: ${data.tableName}`);
  }
  if (data.employeeName) {
    left(`${labels.server}: ${data.employeeName}`);
  }
  left(divider(width));

  for (const item of data.items) {
    const price = formatMoney(item.lineTotal);
    const label = `${item.qty}x ${item.name}`;
    left(twoColumns(label, price, width));
    if (item.qty > 1) {
      left(`   @ ${formatMoney(item.unitPrice)}`);
    }
  }
  left(divider(width));

  left(twoColumns(labels.subtotal, formatMoney(data.subtotal), width));
  if (data.discount > 0) {
    left(twoColumns(labels.discount, `-${formatMoney(data.discount)}`, width));
  }
  if (data.taxTotal > 0) {
    left(twoColumns(labels.tax, formatMoney(data.taxTotal), width));
  }
  left(twoColumns(labels.total, formatMoney(data.total), width), "title");

  if (data.payments.length > 0) {
    left(divider(width));
    for (const p of data.payments) {
      left(twoColumns(p.method, formatMoney(p.amount), width));
      if (p.tip && p.tip > 0) {
        left(twoColumns(`  ${labels.tip}`, formatMoney(p.tip), width));
      }
      if (p.tendered !== undefined && p.tendered > p.amount) {
        left(twoColumns(`  ${labels.tendered}`, formatMoney(p.tendered), width));
      }
      if (p.changeGiven && p.changeGiven > 0) {
        left(twoColumns(`  ${labels.change}`, formatMoney(p.changeGiven), width));
      }
    }
  }

  if (data.footer) {
    center("");
    for (const l of wrapText(data.footer, width)) {
      center(l);
    }
  }

  return lines;
}

/** Preview render: monospaced plain text (one line per entry). */
export function renderReceiptText(lines: ReceiptLine[], width = RECEIPT_WIDTH_80MM): string {
  return lines
    .map((l) => {
      if (l.align === "center" && l.text.length > 0 && l.text.length < width) {
        const pad = Math.floor((width - l.text.length) / 2);
        return " ".repeat(pad) + l.text;
      }
      return l.text;
    })
    .join("\n");
}

/** ESC/POS ticket payload (init + styled lines + feed + cut). */
export function encodeReceiptEscPos(
  lines: ReceiptLine[],
  opts?: { openDrawer?: boolean },
): Uint8Array {
  const b = new EscPosBuilder().init();
  let align: "left" | "center" = "left";
  let bold = false;
  let double = false;
  for (const l of lines) {
    if (l.align !== align) {
      b.align(l.align);
      ({ align } = l);
    }
    const wantBold = l.style === "bold" || l.style === "title";
    if (wantBold !== bold) {
      b.bold(wantBold);
      bold = wantBold;
    }
    const wantDouble = l.style === "title";
    if (wantDouble !== double) {
      b.doubleSize(wantDouble);
      double = wantDouble;
    }
    b.line(l.text);
  }
  if (bold) {
    b.bold(false);
  }
  if (double) {
    b.doubleSize(false);
  }
  b.feed(4);
  if (opts?.openDrawer) {
    b.openDrawer();
  }
  b.cut();
  return b.payload();
}
